import {UpdateUserData} from "@/api";
import {Form} from "../../../utils/Form";
import {
    UpdateExtensionMutation,
    UpdateExtensionMutationVariables,
    useUpdateExtensionMutation,
} from "../queries.generated";
import {areObjectsEqual, getKeys, hasSameElements, MAX_I32} from "@/tools";
import ErrorHelper from "@/utils/ErrorHelper";
import {Error} from "@/api";
import {updateExtensionInputFieldDescriptor} from "./fieldDescriptors";
import {Auth, ContextFilter, useAuth} from "@/context/AuthProvider";
import {StructureConfig, useStructureConfig} from "@/context/StructureConfigProvider";

export default class UpdateExtensionForm extends Form<
    UpdateExtensionMutation,
    UpdateExtensionMutationVariables,
    UpdateUserData
> {
    private readonly id: number;
    private readonly auth: Auth;
    private readonly structureConfig: StructureConfig;
    private readonly isResidential: boolean;
    private readonly extensionContextFilter: ContextFilter;

    constructor(
        id: number,
        initialData: UpdateUserData,
        isResidential: boolean,
        extensionContextFilter: ContextFilter,
    ) {
        super("updateUser");
        this.hooks(useUpdateExtensionMutation, initialData);
        this.id = id;
        this.auth = useAuth();
        this.structureConfig = useStructureConfig();
        this.isResidential = isResidential;
        this.extensionContextFilter = extensionContextFilter;
    }

    protected override get filteredVariables(): UpdateExtensionMutationVariables {
        const values = {...this.values};

        for (const name of getKeys(values)) {
            const desc = updateExtensionInputFieldDescriptor[name];
            if (
                !this.auth.hasPermission(desc.permission, this.extensionContextFilter) ||
                (this.isResidential && desc.businessOnly) ||
                (desc.requiredFeature &&
                    !this.structureConfig.isEnabledFeature(desc.requiredFeature, values.previewFeaturesEnabled))
            )
                delete values[name];
        }

        return {
            id: this.id,
            data: values,
        };
    }

    protected override createErrorHelper(errors: Error[]): ErrorHelper {
        return super.createErrorHelper(errors, 1);
    }

    override updateValues(data: Partial<UpdateUserData>): UpdateUserData {
        if (data["callLimit"])
            data.callLimit = data.callLimit > MAX_I32 ? MAX_I32 : data.callLimit < 0 ? 0 : data.callLimit;
        const newValues = {...this.values, ...data};
        this.setValues(newValues);
        return newValues;
    }

    override get hasChanged(): boolean {
        const initialData = {...this.initialData};
        delete initialData.featureRestrictions;
        delete initialData.groupMembershipGroupIds;
        delete initialData.outgoingCallBarringUser;
        delete initialData.outgoingCallBarringAdmin;
        delete initialData.outgoingCallBarringOperator;

        const values = {...this.values};
        delete values.featureRestrictions;
        delete values.groupMembershipGroupIds;
        delete values.outgoingCallBarringUser;
        delete values.outgoingCallBarringAdmin;
        delete values.outgoingCallBarringOperator;

        return (
            !hasSameElements(this.initialData.featureRestrictions, this.values.featureRestrictions) ||
            !hasSameElements(this.initialData.groupMembershipGroupIds, this.values.groupMembershipGroupIds) ||
            !hasSameElements(this.initialData.outgoingCallBarringUser, this.values.outgoingCallBarringUser) ||
            !hasSameElements(this.initialData.outgoingCallBarringAdmin, this.values.outgoingCallBarringAdmin) ||
            !hasSameElements(this.initialData.outgoingCallBarringOperator, this.values.outgoingCallBarringOperator) ||
            !areObjectsEqual(initialData, values)
        );
    }
}
